import dynamic from 'next/dynamic';
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";
import Link from "next/link";
import Image from "next/image";

const AnimatedSection = dynamic(() => import('../Animations/useAnimations'), { ssr: false });
const ProceduresHeroClient = dynamic(() => import('./ProceduresHeroClient'), { ssr: false });

const ScreenAwareContent = dynamic(() => import('../../utils/screenAwareContent'), { 
  ssr: false,
  loading: () => <div className="min-h-screen h-screen" /> // Simple loading state
});
interface ProceduresHeroData {
  heading?: {
    text?: string;
    title?: string;
  };
  subheading?: {
    text?: string;
    title?: string;
  };
  video?: {
    id: string;
    filename_download: string;
  };
  video_mobile?: {
    id: string;
    filename_download: string;
  };
  content_collection?: {
    id: string;
    site_content_collections_id: {
      collection: {
        collection: string;
        item: {
          title?: string;
          text?: string;
          label?: string;
          href?: string;
        };
      }[];
    };
  }[];
}

export const fields = [
  "heading.text",
  "heading.title",
  
  "subheading.text",
  "subheading.title",
  
  "video.id",
  "video.filename_download",
  
  "video_mobile.id",
  "video_mobile.filename_download",
  
  "content_collection.id",
  "content_collection.site_content_collections_id.collection.collection",
  "content_collection.site_content_collections_id.collection.item:site_content.title",
  "content_collection.site_content_collections_id.collection.item:site_content.text",
  "content_collection.site_content_collections_id.collection.item:menu_items.href",
  "content_collection.site_content_collections_id.collection.item:menu_items.label",
] as const;

interface ProceduresHeroProps {
  data?: ProceduresHeroData;
  zIndex: number;
  thresholds: number[][];
  transitionTime?: number;
  mobileThresholds: number[][];
  helperRotation?: number;
}

export default function ProceduresHero({ data, zIndex, thresholds, transitionTime, mobileThresholds, helperRotation }: ProceduresHeroProps) { 
  const procedureCollections = data?.content_collection || [];
  
  // Prepare collections for client component
  const procedureGroups = procedureCollections.map(collection => {
    const titleItem = collection?.site_content_collections_id?.collection.find(
      item => item.collection === "site_content" && item.item.title
    )?.item;
    
    const textItem = collection?.site_content_collections_id?.collection.find(
      item => item.collection === "site_content" && item.item.text
    )?.item;
    
    const menuItems = collection?.site_content_collections_id?.collection
      .filter(item => item.collection === "menu_items" && item.item.label)
      .map(item => ({
        label: item.item.label || "",
        href: item.item.href || "#"
      }));
    
    return {
      title: titleItem?.title || "",
      description: textItem?.text || "",
      procedures: menuItems || []
    };
  });


  return (
		<ScreenAwareContent
			supportedContent={
				<AnimatedSection
					helperRotation={helperRotation}
					className='h-screen overflow-hidden fixed w-full pt-16 xl:pt-32  top-0 left-0'
					thresholds={[
						thresholds?.[0]?.[0],
						thresholds?.[thresholds.length - 1]?.[1],
					]}
					mobileThresholds={[
						mobileThresholds?.[0]?.[0],
						mobileThresholds?.[mobileThresholds.length - 1]?.[1],
					]}
					zIndex={zIndex}
					transitionTime={transitionTime}
					withZIndex={true}
					variants={{
						desktop: {
							visible: { y: 0, opacity: 1 },
							above: { y: '100vh', opacity: 1 },
							below: { y: '-100vh', opacity: 1 },
						},
						mobile: {
							visible: { y: 0, opacity: 1 },
							above: { y: '100vh', opacity: 1 },
							below: { y: 0, opacity: 0 },
						},
					}}
				>
					<ProceduresHeroClient
						heading={data?.heading?.title || ''}
						subheading={data?.subheading?.title || data?.subheading?.text || ''}
						procedureGroups={procedureGroups}
						mobileThresholds={mobileThresholds}
						videoUrl={
							data?.video?.id
								? `${process.env.NEXT_PUBLIC_DIRECTUS_URL}/assets/${data.video.id}`
								: ''
						}
						videoMobileUrl={
							data?.video_mobile?.id
								? `${process.env.NEXT_PUBLIC_DIRECTUS_URL}/assets/${data.video_mobile.id}`
								: ''
						}
					/>
				</AnimatedSection>
			}
			fallbackContent={
				<div className="w-full relative bg-white">
					{/* Background Video */}
					<div className="absolute left-0 top-0 inset-0 w-full h-full">
						<video
							autoPlay
							muted
							loop
							playsInline
							className="hidden xl:block object-cover object-left w-full h-full"
						>
							<source src={data?.video?.id ? `${process.env.NEXT_PUBLIC_DIRECTUS_URL}/assets/${data.video.id}` : ''} type="video/mp4" />
						</video>
						<video
							autoPlay
							muted
							loop
							playsInline
							className="xl:hidden object-cover w-full h-full"
						>
							<source src={data?.video_mobile?.id ? `${process.env.NEXT_PUBLIC_DIRECTUS_URL}/assets/${data.video_mobile.id}` : ''} type="video/mp4" />
						</video>

						{/* Mobile Overlay */}
						<div className="absolute left-0 top-0 inset-0 w-full h-full bg-primary/50 xl:bg-primary/30" />
					</div>

					{/* Content Container */}
					<div className="px-7 xl:px-0 gl-desktop-scale max-w-[1500px] mx-auto relative py-8 xl:py-16">
						{/* Hero Text with white semi-transparent background */}
						<div className="bg-white/65 backdrop-blur-sm rounded-xl p-8 xl:p-10 mb-0 xl:mb-12 xl:mx-auto xl:max-w-3xl">
							<h2 
								className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] mb-[3vh] max-w-[320px] xl:max-w-none text-primary xl:text-center"
								dangerouslySetInnerHTML={sanitizeHTML(data?.heading?.title || "")}
							/>
							<div 
								className="font-greycliff text-[1rem] !font-normal !leading-normal xl:text-xl xl:leading-snug tracking-tight max-w-[320px] xl:max-w-none text-primary xl:text-center"
								dangerouslySetInnerHTML={sanitizeHTML(data?.subheading?.title || data?.subheading?.text || "")}
							/>
						</div>

						{/* Mobile Buttons */}
						<div className="xl:hidden flex flex-col space-y-4 mt-8">
							{procedureGroups.map((group, index) => (
								<div
									key={index}
									className="w-full bg-white/80 rounded-3xl overflow-hidden"
								>
									<button
										onClick={() => {
											const content = document.getElementById(`content-${index}`);
											const arrow = document.getElementById(`arrow-${index}`);
											
											// Close all other open sections
											procedureGroups.forEach((_, i) => {
												if (i !== index) {
													const otherContent = document.getElementById(`content-${i}`);
													const otherArrow = document.getElementById(`arrow-${i}`);
													if (otherContent && otherArrow) {
														otherContent.classList.add('hidden');
														otherArrow.classList.remove('rotate-[130deg]');
														otherArrow.classList.add('rotate-45');
													}
												}
											});

											// Toggle current section
											if (content && arrow) {
												content.classList.toggle('hidden');
												arrow.classList.toggle('rotate-[130deg]');
												arrow.classList.toggle('rotate-45');
											}
										}}
										className="w-full py-4 px-8 text-primary font-greycliff text-xl flex justify-between items-center"
									>
										<span dangerouslySetInnerHTML={sanitizeHTML(group.title)} />
										<span id={`arrow-${index}`} className="w-2.5 h-2.5 border-r-2 border-t-2 border-primary rotate-45 transition-transform" />
									</button>
									<div id={`content-${index}`} className="hidden p-6">
										<div 
											className="text-primary text-lg font-normal font-greycliff leading-snug tracking-tight mb-4"
											dangerouslySetInnerHTML={sanitizeHTML(group.description)}
										/>
										<div className="flex flex-col gap-2">
											{group.procedures.map((item) => (
												<Link
													key={item.label}
													href={item.href || "#"}
													className="flex justify-between items-center px-4 py-2.5 rounded-full border border-primary text-primary text-lg font-greycliff"
												>
													{item.label}
													<span className="w-2.5 h-2.5 border-r-2 border-t-2 border-primary rotate-45" />
												</Link>
											))}
										</div>
									</div>
								</div>
							))}
						</div>

						{/* Desktop Cards */}
						<div className="hidden xl:flex xl:mt-[6vh] gap-8">
							{procedureGroups.map((group, index) => (
								<div
									key={index}
									className="bg-white/60 rounded-3xl xl:p-[3vh] flex flex-col xl:gap-[3vh] flex-1"
								>
									<h3 
										className="text-primary leading-normal font-greycliff text-[2.1875rem]"
										dangerouslySetInnerHTML={sanitizeHTML(group.title)}
									/>
									<div 
										className="text-primary text-xl font-normal font-greycliff leading-snug tracking-tight"
										dangerouslySetInnerHTML={sanitizeHTML(group.description)}
									/>
									<div className="flex flex-wrap gap-4">
										{group.procedures.map((item) => (
											<Link
												key={item.label}
												href={item.href || "#"}
												className="px-6 py-3 rounded-full border border-primary text-primary text-lg font-greycliff hover:bg-primary hover:text-white transition-colors flex items-center"
											>
												{item.label}
												<Image
													src="/images/arrow.svg"
													alt="Arrow"
													width={26}
													height={27}
													className="ml-2 filter-none hover:[filter:brightness(0)_invert(1)]"
													quality={90}
												/>
											</Link>
										))}
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			}
		/>
	)
}
